import React, {useEffect, useState} from "react";
import { Modal, List, Typography, Spin, Flex, Button } from "antd";
import { ReactComponent as ArrowIcon } from '../../../../../../assets/images/global-icons/arrow_ic.svg';
import { ReactComponent as CrossIcon } from '../../../../../../assets/images/global-icons/cross_ic.svg';
import "./CompanyEmailsModal.scss";
import { ReactComponent as CameraSvg } from '../../../../../../assets/images/global-icons/camera_ic.svg';
import {IValidateCompanyEmailsResponse} from "../../../interfaces/IValidateCompanyEmails";
import {ICompany} from "../../../../../../interfaces/ICompany";
import {ICompanyEmails} from "../../../../../../redux/interfaces/ICompany";

const { Text } = Typography;
const BASE_IMG_URL = process.env.REACT_APP_FILES_BASE_URL + '/';

interface Company {
  companyName: string;
  companyEmail: string;
  companyAddress: string;
  imageUrl: string;
  placeId: string;
  emailName: string;
  link: string;
  address: string;
  picture: string;
  id: string | null;
}

interface CompanyEmails {
  companyEmail: string;
  confidence: string;
  emailName: string;
  position: string | null;
  type: string;
  verificationStatus: string | null;
}

interface CompanyEmailsModalProps {
  visible: boolean;
  onClose: () => void;
  companies: IValidateCompanyEmailsResponse | undefined;
  isLoading: boolean;
  onCompanyClick: (company: Company) => void;
  onBack: () => void;
}

const CompanyEmailsModal: React.FC<CompanyEmailsModalProps> = ({
  visible,
  onClose,
  companies,
  isLoading,
  onCompanyClick,
  onBack
}) => {
  const [company, setCompany] = useState<ICompany>();
  const [emails, setEmails] = useState<ICompanyEmails[]>()

  useEffect(() => {
    if (companies) {
      setCompany(companies.company);
      setEmails(companies.emails);
    }
  }, [companies]);

  return (
    <Modal open={visible} centered footer={null} closable={false} className="companyModal">
      <div className="companyModal_header">
        <ArrowIcon className="companyModal_backIcon" onClick={onBack} />
        <Text className="companyModal_title">Choose a company</Text>
        <CrossIcon className="companyModal_closeIcon" onClick={onClose} />
      </div>

      <div className="companyModal_content">
        {isLoading ? (
          <div className="companyModal_loader">
            <Spin size="large" />
          </div>
        ) : company?.id ? (
          <>
            <Flex vertical>
              <Text className="companyModal_subtitle">Here's what we found</Text>
              <Text className="companyModal_description">Select your company</Text>
            </Flex>
            <List.Item
              className="companyModal_item"
              onClick={() => {
                onCompanyClick({
                  companyName: company.companyName,
                  companyEmail: company.email ?? '',
                  companyAddress: company.address,
                  imageUrl: company.picture,
                  placeId: company.placeId,
                  link: company.link ?? '',
                  emailName: '',
                  address: company.address,
                  id: company.id,
                  picture: company.picture
                });
                onClose();
              }}
            >
              <List.Item.Meta
                title={<Text className="companyModal_name">{company.companyName}</Text>}
                description={<Text className="companyModal_email">{company.address}</Text>}
              />
            </List.Item>
          </>
        ) : emails && emails.length > 0 ? (
          <>
            <Flex vertical>
              <Text className="companyModal_subtitle">Here's what we found</Text>
              <Text className="companyModal_description">Choose your company from the list</Text>
            </Flex>
            <List
              dataSource={emails}
              renderItem={(email) => (
                <List.Item
                  className="companyModal_item"
                  onClick={() => {
                    onCompanyClick({
                      companyName: company?.companyName ?? '',
                      companyEmail: email.companyEmail,
                      companyAddress: company?.address ?? '',
                      imageUrl: company?.picture ?? '',
                      placeId: company?.placeId ?? '',
                      link: company?.link ?? '',
                      emailName: email.emailName,
                      address: company?.address ?? '',
                      id: company?.id ?? null,
                      picture: company?.picture ?? ''
                    });
                    onClose();
                  }}
                >
                  <List.Item.Meta
                    avatar={
                      company?.picture ? (
                        <img
                          className="companyModal__logo"
                          src={company.picture}
                          alt={company.companyName}
                        />
                      ) : (
                        <div className={"companyModal__logo__CameraSvg"}>
                          <CameraSvg />
                        </div>
                      )
                    }
                    title={<Text className="companyModal_name">{email.emailName || email.companyEmail}</Text>}
                    description={<Text className="companyModal_email">{email.companyEmail}</Text>}
                  />
                </List.Item>
              )}
            />
          </>
        ) : (
          <Flex vertical className="companyModal__empty" gap="64px">
            <Flex vertical className="companyModal__empty__header">
              <h3>No results found</h3>
              <p>We couldn't find any company that matches your search</p>
            </Flex>
            <Flex className="companyModal__empty__button" justify="center">
              <Button type="link" onClick={onClose}>
                Try Another Search
              </Button>
            </Flex>
          </Flex>
        )}
      </div>
    </Modal>
  );
};

export default CompanyEmailsModal;
